import { ShieldCheck, FileCheck, TestTube, Boxes, ClipboardCheck, Microscope } from "lucide-react";
import SEO from "@/components/SEO";

const SYSTEMS = [
  { icon: ShieldCheck, title: "Quality Management System", desc: "ISO 9001:2015 aligned QMS with documented SOPs covering every production and laboratory function." },
  { icon: ClipboardCheck, title: "Standard Operating Procedures", desc: "Step-by-step SOPs for manufacturing, packaging, sampling, testing, calibration and deviations." },
  { icon: FileCheck, title: "Batch Documentation", desc: "Complete batch manufacturing and packaging records with full traceability from raw material to despatch." },
];

const TESTS = [
  { icon: TestTube, title: "Raw Material Inspection", desc: "Every incoming material is sampled and tested against pre-approved specifications before release for production." },
  { icon: Microscope, title: "In-Process Quality Control", desc: "Critical parameters (pH, density, viscosity, fill weight, assay) monitored at defined checkpoints during manufacturing." },
  { icon: Boxes, title: "Finished Goods Testing", desc: "Physical, chemical, microbiological and stability testing of every finished batch prior to commercial release." },
];

export default function Quality() {
  return (
    <>
      <SEO title="Quality Assurance" description="Forage's quality systems, raw material inspection, in-process QC, finished goods testing and documentation practices." path="/quality" />

      {/* Header */}
      <section className="bg-[#0B2545] text-white py-24 px-6 md:px-12 lg:px-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container-fr relative">
          <span className="overline text-[#2A9D8F]">Quality Assurance</span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black mt-3 leading-tight max-w-3xl">
            Quality is not a checkpoint. It's our culture.
          </h1>
          <p className="text-lg text-slate-300 mt-6 max-w-2xl">
            From raw material to finished export pallet, every link in our
            chain is built around pharmaceutical-grade testing, documentation
            and accountability.
          </p>
        </div>
      </section>

      {/* Quality Systems */}
      <section className="section-padding bg-white">
        <div className="container-fr">
          <div className="max-w-3xl">
            <span className="overline">Quality Systems</span>
            <h2 className="h-section mt-3">Documented, audited, accountable.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {SYSTEMS.map((s, i) => (
              <div key={s.title} className="p-7 rounded-xl border border-slate-200 card-lift fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <div className="w-11 h-11 rounded-lg bg-[#2A9D8F]/10 text-[#2A9D8F] flex items-center justify-center mb-5">
                  <s.icon size={22} />
                </div>
                <h3 className="font-display text-xl font-bold text-[#0B2545]">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testing flow */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="container-fr grid lg:grid-cols-2 gap-14 items-center">
          <img src="https://images.pexels.com/photos/8851634/pexels-photo-8851634.jpeg" alt="QC Laboratory" loading="lazy" className="rounded-xl object-cover w-full h-[520px] shadow-lg" />
          <div>
            <span className="overline">Testing Procedures</span>
            <h2 className="h-section mt-3">Three layers of quality control.</h2>
            <div className="mt-8 space-y-7">
              {TESTS.map((t, i) => (
                <div key={t.title} className="flex gap-5">
                  <div className="w-12 h-12 rounded-lg bg-[#0B2545] text-[#2A9D8F] flex items-center justify-center flex-shrink-0">
                    <t.icon size={22} />
                  </div>
                  <div>
                    <div className="text-xs text-[#2A9D8F] font-semibold uppercase tracking-wider">Step {i + 1}</div>
                    <h3 className="font-display text-xl font-bold text-[#0B2545] mt-1">{t.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Documentation */}
      <section className="section-padding bg-[#0B2545] text-white">
        <div className="container-fr grid lg:grid-cols-2 gap-14">
          <div>
            <span className="overline text-[#2A9D8F]">Documentation Practices</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mt-3 leading-tight">
              Traceable. Auditable. Exportable.
            </h2>
            <p className="text-slate-300 mt-6 leading-relaxed">
              Every batch we manufacture is supported by a comprehensive set of
              documents — Batch Manufacturing Record, Batch Packaging Record,
              Certificate of Analysis, Material Safety Data Sheet and
              shipment-ready export documentation. Documents are version
              controlled and archived per regulatory retention norms.
            </p>
          </div>
          <ul className="space-y-4">
            {[
              "Batch Manufacturing Records (BMR)",
              "Batch Packaging Records (BPR)",
              "Certificate of Analysis (CoA) for every batch",
              "Material Safety Data Sheets (MSDS)",
              "Stability study protocols & reports",
              "Calibration & validation records",
              "Deviation, change control and CAPA logs",
              "Export documentation (COO, COPP, free sale certificates)",
            ].map((d) => (
              <li key={d} className="flex gap-3 items-start text-slate-200">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2A9D8F] mt-2.5 flex-shrink-0" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
